// G-2 action-plan tasks on a Pursuit. This is a list, not a kanban and not
// the workstream board. Status is the task row's own open|in_progress|done|parked
// — never capture_projects.stage. Owner is an optional user id string, not a
// Person registry chip (G-3).
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, Plus, RotateCcw, Sparkles, X } from 'lucide-react';
import { useState } from 'react';
import { PlatformUserPicker } from '../Delivery/_shared/components/PlatformUserPicker';
import { CaptureTaskOrigin } from './CaptureTaskOrigin';
import { CAPTURE_PLAN_HEALTH_STATUSES, formatTs, labelOf, TASK_STATUSES, workstreamLabel } from './constants';
import { useCapturePlan, useCaptureTaskProvenance, useDeleteCapturePlanArea, useDeleteCaptureTask, useSetCapturePlanAreaHealth, useSetCaptureTaskPlanArea, useSetCaptureTaskProvenance, useUpsertCapturePlanArea, useUpsertCaptureTask } from './hooks';
import { ReadOnlyScope } from './record/ReadOnlyScope';
const STATUS_STYLE = {
    open: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-amber-50 text-amber-800',
    done: 'bg-green-50 text-green-700',
    parked: 'bg-indigo-50 text-indigo-700'
};
const HEALTH_STYLE = {
    not_started: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-amber-50 text-amber-800',
    at_risk: 'bg-red-50 text-danger',
    complete: 'bg-green-50 text-green-700',
    not_applicable: 'bg-indigo-50 text-indigo-700'
};
// Past its due day and still actionable. The due date is a local calendar day,
// so compare day strings, not instants. There is no server-side sweeper.
function isOverdue(t, now = new Date()) {
    if (!t.dueAt || t.status === 'done' || t.status === 'parked') return false;
    return dateInputValue(t.dueAt) < localDay(now);
}
function toRowError(err, retry) {
    if (err instanceof ConnectError && err.code === Code.InvalidArgument) {
        return {
            kind: 'owner',
            message: err.rawMessage,
            retry
        };
    }
    return {
        kind: 'network',
        message: 'Could not save the change. Check your connection and retry.',
        retry
    };
}
function dateInputValue(ts) {
    return ts ? localDay(ts.toDate()) : '';
}
function localDay(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function parseDateInput(value) {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}
// One Capture Plan area row: name/lead/target/summary, its health badge (set
// only through Save health below), an unapplied AI suggestion banner (Accept
// routes through the same Save path), and its linked tasks. Completing a
// linked task in the Action plan list above never touches any of this.
function CapturePlanAreaRow({ area, captureProjectId, tasks, provenance, correlationId }) {
    const upsertTask = useUpsertCaptureTask(correlationId);
    const setProvenance = useSetCaptureTaskProvenance(correlationId);
    const [taskTitle, setTaskTitle] = useState('');
    const setHealth = useSetCapturePlanAreaHealth(correlationId);
    const remove = useDeleteCapturePlanArea(correlationId);
    const [draftHealth, setDraftHealth] = useState(area.health || 'not_started');
    const [reason, setReason] = useState('');
    const suggestion = area.recentHealthEvents.find((e)=>e.origin === 'ai' && !e.applied);
    const areaTasks = tasks.filter((t)=>area.taskIds.includes(t.id));
    // A task created from inside an area came FROM that area: origin 'area',
    // ref = this area. Stamped right after the task exists.
    const addAreaTask = ()=>{
        const trimmed = taskTitle.trim();
        if (!trimmed) return;
        upsertTask.mutate({
            captureProjectId,
            title: trimmed,
            status: 'open'
        }, {
            onSuccess: (resp)=>{
                setTaskTitle('');
                if (!resp.task?.id) return;
                setProvenance.mutate({
                    captureProjectId,
                    taskId: resp.task.id,
                    areaKey: area.id,
                    originKind: 'area',
                    originRefId: area.id
                });
            }
        });
    };
    const saveHealth = (health, reasons)=>{
        setHealth.mutate({
            captureProjectId,
            areaId: area.id,
            health,
            reasons
        }, {
            onSuccess: ()=>setReason('')
        });
    };
    return /*#__PURE__*/ _jsxs("li", {
        className: "space-y-3 rounded-md border p-3 text-sm",
        "data-testid": Testids.capture.planAreaRow,
        "data-area-id": area.id,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-start justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1 space-y-0.5",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "font-medium",
                                children: area.name
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    area.leadUserId || 'Unassigned',
                                    area.targetDate ? ` · target ${formatTs(area.targetDate)}` : ''
                                ]
                            }),
                            area.summary && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: area.summary
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs ${HEALTH_STYLE[area.health] ?? HEALTH_STYLE.not_started}`,
                                "data-testid": Testids.capture.planAreaHealthBadge,
                                children: labelOf(CAPTURE_PLAN_HEALTH_STATUSES, area.health || 'not_started')
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                size: "sm",
                                "data-testid": Testids.capture.planAreaDeleteButton,
                                disabled: remove.isPending,
                                onClick: ()=>remove.mutate({
                                        captureProjectId,
                                        id: area.id
                                    }),
                                iconLeft: /*#__PURE__*/ _jsx(X, {
                                    className: "h-3.5 w-3.5"
                                }),
                                children: "Remove"
                            })
                        ]
                    })
                ]
            }),
            suggestion && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border border-blue-200 bg-blue-50 p-2 text-xs",
                "data-testid": Testids.capture.planSuggestionBanner,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1 font-medium text-blue-800",
                        children: [
                            /*#__PURE__*/ _jsx(Sparkles, {
                                className: "h-3.5 w-3.5"
                            }),
                            "AI suggests ",
                            labelOf(CAPTURE_PLAN_HEALTH_STATUSES, suggestion.proposedHealth)
                        ]
                    }),
                    suggestion.reasons && /*#__PURE__*/ _jsx("p", {
                        className: "text-blue-700",
                        children: suggestion.reasons
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        "data-testid": Testids.capture.planSuggestionAcceptButton,
                        disabled: setHealth.isPending,
                        onClick: ()=>saveHealth(suggestion.proposedHealth, suggestion.reasons),
                        children: "Accept"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-40 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                htmlFor: `capture-plan-health-${area.id}`,
                                children: "Health"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: draftHealth,
                                onValueChange: setDraftHealth,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        id: `capture-plan-health-${area.id}`,
                                        "data-testid": Testids.capture.planAreaHealthSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: CAPTURE_PLAN_HEALTH_STATUSES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-[180px] flex-1 space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("label", {
                                className: "text-xs font-medium",
                                htmlFor: `capture-plan-reason-${area.id}`,
                                children: "Reason"
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                id: `capture-plan-reason-${area.id}`,
                                value: reason,
                                onChange: (e)=>setReason(e.target.value),
                                placeholder: "Why this health",
                                "data-testid": Testids.capture.planAreaHealthReasonInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        "data-testid": Testids.capture.planAreaHealthSaveButton,
                        disabled: setHealth.isPending,
                        onClick: ()=>saveHealth(draftHealth, reason),
                        children: "Save health"
                    })
                ]
            }),
            areaTasks.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 border-t pt-2",
                children: areaTasks.map((t)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex items-center justify-between gap-2 text-xs",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: t.title
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "flex items-center gap-1",
                                children: [
                                    /*#__PURE__*/ _jsx(CaptureTaskOrigin, {
                                        provenance: provenance.get(t.id),
                                        areaName: area.name,
                                        captureProjectId: captureProjectId
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `rounded-full px-2 py-0.5 ${STATUS_STYLE[t.status] ?? STATUS_STYLE.open}`,
                                        children: labelOf(TASK_STATUSES, t.status)
                                    })
                                ]
                            })
                        ]
                    }, t.id))
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 border-t pt-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        value: taskTitle,
                        onChange: (e)=>setTaskTitle(e.target.value),
                        placeholder: "Add a task to this area",
                        "aria-label": `Add a task to ${area.name}`,
                        "data-testid": Testids.capture.planAreaTaskInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        onClick: addAreaTask,
                        disabled: !taskTitle.trim() || upsertTask.isPending || setProvenance.isPending,
                        "data-testid": Testids.capture.planAreaTaskAddButton,
                        iconLeft: /*#__PURE__*/ _jsx(Plus, {
                            className: "h-4 w-4"
                        }),
                        children: "Add task"
                    })
                ]
            })
        ]
    });
}
export function CaptureTasksPanel({ captureProjectId, tasks, correlationId, readOnly = false }) {
    const upsert = useUpsertCaptureTask(correlationId);
    const remove = useDeleteCaptureTask(correlationId);
    const [title, setTitle] = useState('');
    const [owner, setOwner] = useState('');
    const [due, setDue] = useState('');
    const plan = useCapturePlan(captureProjectId);
    const addArea = useUpsertCapturePlanArea(correlationId);
    const setTaskArea = useSetCaptureTaskPlanArea(correlationId);
    const setProvenance = useSetCaptureTaskProvenance(correlationId);
    // A failed provenance load degrades to "no badge", never to an error page.
    const provenanceQuery = useCaptureTaskProvenance(captureProjectId);
    const provenanceByTask = new Map((provenanceQuery.isError ? [] : provenanceQuery.data ?? []).map((p)=>[
            p.taskId,
            p
        ]));
    const [rowErrors, setRowErrors] = useState({});
    const setRowError = (taskId, err)=>setRowErrors((prev)=>{
            const next = {
                ...prev
            };
            if (err) next[taskId] = err;
            else delete next[taskId];
            return next;
        });
    const [areaName, setAreaName] = useState('');
    const [areaLead, setAreaLead] = useState('');
    const [areaTarget, setAreaTarget] = useState('');
    const [areaSummary, setAreaSummary] = useState('');
    const areas = plan.data?.areas ?? [];
    // One mutation in flight at a time across the list: no double submit.
    const busy = upsert.isPending || setProvenance.isPending || setTaskArea.isPending;
    const upcomingReviews = plan.data?.upcomingReviews ?? [];
    const upcomingDecisions = plan.data?.upcomingDecisions ?? [];
    const references = plan.data?.references;
    // The projection (area.taskIds), not a field on the task itself — a task
    // doesn't carry its own area id, only an area's taskIds names its tasks.
    const areaIdForTask = (taskId)=>areas.find((a)=>a.taskIds.includes(taskId))?.id ?? '';
    const submitArea = ()=>{
        const trimmed = areaName.trim();
        if (!trimmed) return;
        addArea.mutate({
            captureProjectId,
            name: trimmed,
            leadUserId: areaLead.trim(),
            targetDate: parseDateInput(areaTarget),
            summary: areaSummary.trim()
        }, {
            onSuccess: ()=>{
                setAreaName('');
                setAreaLead('');
                setAreaTarget('');
                setAreaSummary('');
            }
        });
    };
    // The upsert replaces the whole row, so every update re-sends every field of
    // the task being edited and only the patched ones change. Same id, same row:
    // reassign and reopen never create a second task, and area links (a separate
    // table keyed by task id) and area health are untouched.
    // On failure the row shows the problem inline and, because the owner picker
    // is controlled by the stored owner, snaps back to the previous owner. The
    // next successful change clears the message.
    const saveTask = (t, patch)=>upsert.mutate({
            captureProjectId,
            id: t.id,
            title: t.title,
            ownerUserId: patch.ownerUserId ?? t.ownerUserId,
            dueAt: t.dueAt ? parseDateInput(dateInputValue(t.dueAt)) : null,
            status: patch.status ?? t.status,
            notes: t.notes,
            sortOrder: t.sortOrder
        }, {
            onSuccess: ()=>setRowError(t.id, null),
            onError: (err)=>setRowError(t.id, toRowError(err, ()=>saveTask(t, patch)))
        });
    // Changing the area never rewrites where the task came from: the stored
    // origin is sent back unchanged (a task with none on record is 'manual').
    // If provenance did not load the origin is unknown, so only the area link
    // moves, through the older plan-area call.
    const changeArea = (t, areaId)=>{
        if (provenanceQuery.isError || !provenanceQuery.data) {
            setTaskArea.mutate({
                captureProjectId,
                taskId: t.id,
                areaId
            });
            return;
        }
        const prev = provenanceByTask.get(t.id);
        setProvenance.mutate({
            captureProjectId,
            taskId: t.id,
            areaKey: areaId,
            originKind: prev?.originKind || 'manual',
            originRefId: prev?.originRefId ?? ''
        }, {
            onSuccess: ()=>setRowError(t.id, null),
            onError: (err)=>setRowError(t.id, toRowError(err, ()=>changeArea(t, areaId)))
        });
    };
    const add = ()=>{
        const trimmed = title.trim();
        if (!trimmed) return;
        upsert.mutate({
            captureProjectId,
            title: trimmed,
            ownerUserId: owner.trim(),
            dueAt: parseDateInput(due),
            status: 'open'
        }, {
            onSuccess: ()=>{
                setTitle('');
                setOwner('');
                setDue('');
            }
        });
    };
    return /*#__PURE__*/ _jsxs(ReadOnlyScope, {
        readOnly: readOnly,
        children: [
            /*#__PURE__*/ _jsxs(SpecraCard, {
                "data-testid": Testids.capture.tasksPanel,
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        className: "py-4",
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Action plan"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Tasks on this pursuit — call Jane, draft CONOPS, send NDA. Not a pipeline board and not the workstream artifact tracker."
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-end gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-[180px] flex-1 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                htmlFor: "capture-task-title",
                                                children: "Title"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "capture-task-title",
                                                value: title,
                                                onChange: (e)=>setTitle(e.target.value),
                                                placeholder: "Call Jane",
                                                "data-testid": Testids.capture.tasksTitleInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-40 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                htmlFor: "capture-task-owner",
                                                children: "Owner"
                                            }),
                                            /*#__PURE__*/ _jsx(PlatformUserPicker, {
                                                id: "capture-task-owner",
                                                value: owner,
                                                onChange: setOwner,
                                                placeholder: "Search people…",
                                                clearLabel: "Unassigned",
                                                testid: Testids.capture.tasksOwnerSearch,
                                                triggerTestid: Testids.capture.tasksOwnerInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-40 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                htmlFor: "capture-task-due",
                                                children: "Due"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "capture-task-due",
                                                type: "date",
                                                value: due,
                                                onChange: (e)=>setDue(e.target.value),
                                                "data-testid": Testids.capture.tasksDueInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        onClick: add,
                                        disabled: !title.trim() || upsert.isPending,
                                        "data-testid": Testids.capture.tasksAddButton,
                                        iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                            className: "h-4 w-4"
                                        }),
                                        children: "Add"
                                    })
                                ]
                            }),
                            tasks.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "No action-plan tasks yet."
                            }) : /*#__PURE__*/ _jsx("ul", {
                                className: "space-y-2",
                                children: tasks.map((t)=>/*#__PURE__*/ _jsxs("li", {
                                        className: "flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm",
                                        "data-testid": Testids.capture.tasksRow,
                                        "data-task-id": t.id,
                                        id: `capture-task-${t.id}`,
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "min-w-0 flex-1 space-y-0.5",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "font-medium",
                                                        children: t.title
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex flex-wrap items-center gap-1 text-xs text-muted-foreground",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("span", {
                                                                children: t.dueAt ? `due ${formatTs(t.dueAt)}` : 'No due date'
                                                            }),
                                                            isOverdue(t) && /*#__PURE__*/ _jsx(SpecraBadge, {
                                                                variant: "danger",
                                                                "data-testid": Testids.capture.tasksOverdueBadge,
                                                                children: "Overdue"
                                                            }),
                                                            /*#__PURE__*/ _jsx(CaptureTaskOrigin, {
                                                                captureProjectId: captureProjectId,
                                                                provenance: provenanceByTask.get(t.id),
                                                                areaName: areas.find((a)=>a.id === provenanceByTask.get(t.id)?.originRefId)?.name
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "w-40",
                                                children: /*#__PURE__*/ _jsx(PlatformUserPicker, {
                                                    id: `capture-task-owner-${t.id}`,
                                                    value: t.ownerUserId,
                                                    onChange: (ownerUserId)=>saveTask(t, {
                                                            ownerUserId
                                                        }),
                                                    disabled: busy,
                                                    placeholder: "Unassigned",
                                                    clearLabel: "Unassigned",
                                                    testid: `capture-task-owner-search-${t.id}`
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[t.status] ?? STATUS_STYLE.open}`,
                                                children: labelOf(TASK_STATUSES, t.status)
                                            }),
                                            areas.length > 0 && /*#__PURE__*/ _jsxs(Select, {
                                                value: areaIdForTask(t.id),
                                                disabled: busy,
                                                onValueChange: (areaId)=>changeArea(t, areaId),
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        className: "h-8 w-36",
                                                        "data-testid": Testids.capture.tasksAreaSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                                            placeholder: "No area"
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: areas.map((a)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: a.id,
                                                                children: a.name
                                                            }, a.id))
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    t.status !== 'done' && /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "ghost",
                                                        size: "sm",
                                                        "data-testid": Testids.capture.tasksCompleteButton,
                                                        disabled: busy,
                                                        onClick: ()=>saveTask(t, {
                                                                status: 'done'
                                                            }),
                                                        iconLeft: /*#__PURE__*/ _jsx(Check, {
                                                            className: "h-3.5 w-3.5"
                                                        }),
                                                        children: "Complete"
                                                    }),
                                                    t.status === 'done' && /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "ghost",
                                                        size: "sm",
                                                        disabled: busy,
                                                        onClick: ()=>saveTask(t, {
                                                                status: 'open'
                                                            }),
                                                        iconLeft: /*#__PURE__*/ _jsx(RotateCcw, {
                                                            className: "h-3.5 w-3.5"
                                                        }),
                                                        children: "Reopen"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "ghost",
                                                        size: "sm",
                                                        "data-testid": Testids.capture.tasksDeleteButton,
                                                        disabled: remove.isPending,
                                                        onClick: ()=>remove.mutate({
                                                                captureProjectId,
                                                                id: t.id
                                                            }),
                                                        iconLeft: /*#__PURE__*/ _jsx(X, {
                                                            className: "h-3.5 w-3.5"
                                                        }),
                                                        children: "Remove"
                                                    })
                                                ]
                                            }),
                                            rowErrors[t.id] && /*#__PURE__*/ _jsxs("div", {
                                                className: "flex basis-full items-center gap-2 text-xs text-danger",
                                                role: "alert",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        "data-testid": rowErrors[t.id].kind === 'owner' ? Testids.capture.tasksOwnerError : undefined,
                                                        children: rowErrors[t.id].message
                                                    }),
                                                    rowErrors[t.id].kind === 'network' && /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "ghost",
                                                        size: "sm",
                                                        disabled: busy,
                                                        onClick: rowErrors[t.id].retry,
                                                        "data-testid": Testids.capture.tasksRetryButton,
                                                        children: "Retry"
                                                    })
                                                ]
                                            })
                                        ]
                                    }, t.id))
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraCard, {
                "data-testid": Testids.capture.planPanel,
                children: [
                    /*#__PURE__*/ _jsx(SpecraCardHeader, {
                        className: "py-4",
                        children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Capture Plan"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SpecraCardContent, {
                        className: "space-y-4",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "A rollup of this pursuit's Capture Areas, tasks, upcoming reviews and decisions — not a duplicate store. Area health is a human judgment call; completing a linked task never moves it."
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap items-end gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "min-w-[160px] flex-1 space-y-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("label", {
                                                        className: "text-xs font-medium",
                                                        htmlFor: "capture-plan-area-name",
                                                        children: "Area name"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        id: "capture-plan-area-name",
                                                        value: areaName,
                                                        onChange: (e)=>setAreaName(e.target.value),
                                                        placeholder: "Solutioning",
                                                        "data-testid": Testids.capture.planAreaNameInput
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "w-36 space-y-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("label", {
                                                        className: "text-xs font-medium",
                                                        htmlFor: "capture-plan-area-lead",
                                                        children: "Lead"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        id: "capture-plan-area-lead",
                                                        value: areaLead,
                                                        onChange: (e)=>setAreaLead(e.target.value),
                                                        placeholder: "User id",
                                                        "data-testid": Testids.capture.planAreaLeadInput
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "w-36 space-y-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("label", {
                                                        className: "text-xs font-medium",
                                                        htmlFor: "capture-plan-area-target",
                                                        children: "Target"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        id: "capture-plan-area-target",
                                                        type: "date",
                                                        value: areaTarget,
                                                        onChange: (e)=>setAreaTarget(e.target.value),
                                                        "data-testid": Testids.capture.planAreaTargetInput
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "min-w-[180px] flex-1 space-y-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx("label", {
                                                        className: "text-xs font-medium",
                                                        htmlFor: "capture-plan-area-summary",
                                                        children: "Summary"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        id: "capture-plan-area-summary",
                                                        value: areaSummary,
                                                        onChange: (e)=>setAreaSummary(e.target.value),
                                                        placeholder: "What this area covers",
                                                        "data-testid": Testids.capture.planAreaSummaryInput
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                onClick: submitArea,
                                                disabled: !areaName.trim() || addArea.isPending,
                                                "data-testid": Testids.capture.planAreaAddButton,
                                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                                    className: "h-4 w-4"
                                                }),
                                                children: "Add area"
                                            })
                                        ]
                                    }),
                                    areas.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-muted-foreground",
                                        children: "No capture areas yet."
                                    }) : /*#__PURE__*/ _jsx("ul", {
                                        className: "space-y-2",
                                        children: areas.map((a)=>/*#__PURE__*/ _jsx(CapturePlanAreaRow, {
                                                area: a,
                                                captureProjectId: captureProjectId,
                                                tasks: tasks,
                                                provenance: provenanceByTask,
                                                correlationId: correlationId
                                            }, a.id))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid gap-3 sm:grid-cols-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        "data-testid": Testids.capture.planUpcomingReviews,
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-xs font-medium text-muted-foreground",
                                                children: "Upcoming reviews"
                                            }),
                                            upcomingReviews.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No upcoming reviews."
                                            }) : /*#__PURE__*/ _jsx("ul", {
                                                className: "space-y-1",
                                                children: upcomingReviews.map((r)=>/*#__PURE__*/ _jsxs("li", {
                                                        className: "text-sm",
                                                        children: [
                                                            r.label || r.milestoneType,
                                                            " · ",
                                                            formatTs(r.scheduledAt)
                                                        ]
                                                    }, r.id))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        "data-testid": Testids.capture.planUpcomingDecisions,
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-xs font-medium text-muted-foreground",
                                                children: "Upcoming decisions"
                                            }),
                                            upcomingDecisions.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No upcoming decisions."
                                            }) : /*#__PURE__*/ _jsx("ul", {
                                                className: "space-y-1",
                                                children: upcomingDecisions.map((d)=>/*#__PURE__*/ _jsxs("li", {
                                                        className: "text-sm",
                                                        children: [
                                                            "Due ",
                                                            formatTs(d.dueAt),
                                                            " · ",
                                                            d.notes || 'No notes'
                                                        ]
                                                    }, d.id))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                "data-testid": Testids.capture.planReferences,
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-xs font-medium text-muted-foreground",
                                        children: "References"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs font-semibold text-muted-foreground",
                                                children: "Partners"
                                            }),
                                            !references || references.partners.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No partners linked."
                                            }) : /*#__PURE__*/ _jsx("ul", {
                                                className: "flex flex-wrap gap-2",
                                                children: references.partners.map((p)=>/*#__PURE__*/ _jsx("li", {
                                                        className: "inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700",
                                                        children: p.companyProfileId
                                                    }, p.teamingLinkId))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs font-semibold text-muted-foreground",
                                                children: "Solutions & outputs"
                                            }),
                                            !references || references.solutionsAndOutputs.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No solution outputs linked."
                                            }) : /*#__PURE__*/ _jsx("ul", {
                                                className: "flex flex-wrap gap-2",
                                                children: references.solutionsAndOutputs.map((s)=>/*#__PURE__*/ _jsxs("li", {
                                                        className: "inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700",
                                                        children: [
                                                            s.title,
                                                            " · ",
                                                            workstreamLabel(s.workstreamType)
                                                        ]
                                                    }, s.workstreamItemId))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs font-semibold text-muted-foreground",
                                                children: "Customer"
                                            }),
                                            references?.customer?.captureProfileId ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm",
                                                children: references.customer.customerAgencyNames || 'Customer profile'
                                            }) : /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-muted-foreground",
                                                children: "No customer profile recorded."
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
